import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Card, Col, Row, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PageHeader from 'components/common/PageHeader';
import axiosInstance from 'axiosInstance';

const SalesReportsPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      setLoading(true);
      setError('');
      try {
        const overviewResponse = await axiosInstance.get('sales/reports/overview/');

        if (!mounted) return;

        setOverview(overviewResponse.data || null);
      } catch (requestError) {
        if (!mounted) return;
        setError(requestError?.response?.data?.error || t('SALES.ERROR_LOAD_REPORTS'));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();
    return () => {
      mounted = false;
    };
  }, [t]);

  const summary = useMemo(() => {
    const opportunities = overview?.opportunities || {};
    const tasks = overview?.tasks || {};
    const accounts = overview?.accounts || {};

    return {
      open: opportunities.open || 0,
      won: opportunities.won || 0,
      lost: opportunities.lost || 0,
      hot: opportunities.stale_without_next_step || 0,
      pendingTasks: (tasks.total || 0) - (tasks.done || 0),
      doneTasks: tasks.done || 0,
      accountsToActivate: accounts.pending_activation || 0,
      conversionRate: opportunities.conversion_rate_percent || 0,
      averageCycleDays: opportunities.average_cycle_days || 0,
      taskCompletionRate: tasks.completion_rate_percent || 0,
      byStage: opportunities.by_stage || {}
    };
  }, [overview]);

  return (
    <>
      <PageHeader title={t('SALES.REPORTS')} className="mb-3" />

      {error ? <Alert variant="danger">{error}</Alert> : null}

      {loading ? (
        <Card className="p-4 text-center"><Spinner animation="border" /></Card>
      ) : (
        <>
          <Row className="g-3 mb-3">
            <Col md={6} xl={2}>
              <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.OPEN')}</div><div className="fs-2 fw-bold">{summary.open}</div></Card.Body></Card>
            </Col>
            <Col md={6} xl={2}>
              <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.WON')}</div><div className="fs-2 fw-bold text-success">{summary.won}</div></Card.Body></Card>
            </Col>
            <Col md={6} xl={2}>
              <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.LOST')}</div><div className="fs-2 fw-bold text-danger">{summary.lost}</div></Card.Body></Card>
            </Col>
            <Col md={6} xl={2}>
              <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.STALE_NO_NEXT_STEP')}</div><div className="fs-2 fw-bold text-warning">{summary.hot}</div></Card.Body></Card>
            </Col>
            <Col md={6} xl={2}>
              <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.PENDING_TASKS')}</div><div className="fs-2 fw-bold text-primary">{summary.pendingTasks}</div></Card.Body></Card>
            </Col>
            <Col md={6} xl={2}>
              <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.ACCOUNTS_TO_ACTIVATE')}</div><div className="fs-2 fw-bold">{summary.accountsToActivate}</div></Card.Body></Card>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md={4}>
              <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.CONVERSION_RATE')}</div><div className="fs-2 fw-bold">{summary.conversionRate}%</div></Card.Body></Card>
            </Col>
            <Col md={4}>
              <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.AVG_CYCLE_DAYS')}</div><div className="fs-2 fw-bold">{summary.averageCycleDays}</div></Card.Body></Card>
            </Col>
            <Col md={4}>
              <Card className="h-100"><Card.Body><div className="text-600 fs--1">{t('SALES.TASK_COMPLETION_RATE')}</div><div className="fs-2 fw-bold">{summary.taskCompletionRate}%</div></Card.Body></Card>
            </Col>
          </Row>

          <Card>
            <Card.Header className="fw-semi-bold">{t('SALES.PIPELINE_DISTRIBUTION')}</Card.Header>
            <Card.Body className="pt-2">
              {Object.keys(summary.byStage).length ? (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th>{t('SALES.PIPELINE_STAGE')}</th>
                      <th>{t('SALES.COUNT')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(summary.byStage).map(([stageName, count]) => (
                      <tr key={stageName}>
                        <td>{stageName}</td>
                        <td><Badge bg="soft-info" text="info">{count}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-600">{t('SALES.EMPTY_OPPORTUNITIES')}</div>
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </>
  );
};

export default SalesReportsPage;
